/**
 * SubagentModal — drill into one sub-agent's own stream.
 *
 * When the model delegates via the ``Task`` tool, the sub-agent's reasoning,
 * tool calls and text are deliberately kept OUT of the main transcript (they
 * used to masquerade as the main agent's own rows — see schema v35). This
 * modal is where that work becomes visible: open it from the sub-agent's row
 * in the "Working" indicator, or from its ``Task`` tool card.
 *
 * v1 polls while the sub-agent is running rather than streaming over the WS:
 * the rows are already persisted per turn, so a short poll gives a live-enough
 * view with no new protocol surface. The poll stops the moment the sub-agent
 * reaches a terminal state (it disappears from the store's ``subagents`` map).
 *
 * Chrome follows the two established modal patterns in the codebase: Esc +
 * ``aria-modal`` + open-guard from SearchModal, backdrop + stopPropagation
 * from ShareDialog. Content reuses the ordinary transcript renderers so a
 * sub-agent's stream looks exactly like the chat it came from.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { fetchSubagentTranscript } from "@/api/subagents";
import type { Message, ToolCallState } from "@/api/types";
import { useChatStore } from "@/stores/chatStore";
import { useSubagentDrillStore } from "@/stores/subagentDrillStore";
import { toolMeta } from "@/theme";
import { MessageBubble } from "./MessageBubble";
import { MessageThinkingPanel } from "./ThinkingPanel";
import { ToolCallBlock } from "./ToolCallBlock";
import { buildToolResultMap, resolveToolCall } from "./transcriptShared";

/** How often to re-read the transcript while the sub-agent is still working. */
const POLL_MS = 2000;

/** One renderable row of a sub-agent's stream. */
type SubRow =
  | { kind: "text"; msg: Message }
  | { kind: "thinking"; id: string; text: string; durationMs: number | null }
  | { kind: "tool"; id: string; call: ToolCallState };

/** Map raw rows into renderable ones, pairing tool calls with their results. */
function toRows(messages: Message[], live: boolean): SubRow[] {
  const resultMap = buildToolResultMap(messages);
  const rows: SubRow[] = [];
  for (const msg of messages) {
    if (msg.role === "tool_result") continue;   // folded into its tool card
    if (msg.role === "thinking") {
      try {
        const blocks = JSON.parse(msg.content_json);
        const block = blocks.find((b: { type: string }) => b.type === "thinking");
        if (block?.thinking) {
          rows.push({
            kind: "thinking",
            id: msg.id,
            text: block.thinking,
            durationMs: block.duration_ms ?? null,
          });
        }
      } catch { /* ignore malformed row */ }
      continue;
    }
    if (msg.role === "tool_use") {
      // No live WS map here — a tool with no result row yet is still running,
      // which is the normal case while polling an active sub-agent.
      const call = resolveToolCall(msg, resultMap, undefined, {
        unresolvedIsRunning: live,
      });
      if (call) rows.push({ kind: "tool", id: msg.id, call });
      continue;
    }
    rows.push({ kind: "text", msg });
  }
  return rows;
}

export function SubagentModal() {
  const openId = useSubagentDrillStore((s) => s.openId);
  const close = useSubagentDrillStore((s) => s.close);
  const conversationId = useChatStore((s) => s.activeConversationId);
  // Live metadata for the header (type, current activity, tokens). Present
  // only while the sub-agent runs; absent once it finishes, which is also the
  // signal to stop polling.
  const info = useChatStore((s) => (openId ? s.subagents.get(openId) : undefined));
  const isRunning = info?.status === "running";

  const [messages, setMessages] = useState<Message[] | null>(null);
  const [report, setReport] = useState<{ content: string; is_error: boolean } | null>(null);
  const [error, setError] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Fetch on open, then re-poll while the sub-agent is still working.
  useEffect(() => {
    if (!openId || !conversationId) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const load = () => {
      fetchSubagentTranscript(conversationId, openId)
        .then((d) => {
          if (!alive) return;
          setMessages(d.messages);
          setReport(d.final_report);
          setError(false);
        })
        .catch(() => alive && setError(true))
        .finally(() => {
          // Chain the next poll only after the previous one settles, so a slow
          // response can't stack overlapping requests.
          if (alive && isRunning) timer = setTimeout(load, POLL_MS);
        });
    };
    load();

    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [openId, conversationId, isRunning]);

  // Reset between sub-agents so the previous one's rows never flash.
  useEffect(() => {
    setMessages(null);
    setReport(null);
    setError(false);
  }, [openId]);

  // Esc closes; focus the dialog so the key lands without a click first.
  useEffect(() => {
    if (!openId) return;
    dialogRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openId, close]);

  const rows = useMemo(
    () => (messages ? toRows(messages, isRunning) : []),
    [messages, isRunning],
  );

  // Synthesised so the report can reuse MessageBubble. Memoised on the content
  // itself — a new object each poll would remount the bubble every 2s.
  const reportMessage = useMemo<Message | null>(
    () =>
      report && openId
        ? {
            id: `subagent-report-${openId}`,
            role: "assistant",
            content_json: JSON.stringify([{ type: "text", text: report.content }]),
            created_at: new Date().toISOString(),
          }
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [report?.content, openId],
  );

  if (!openId) return null;

  const title = info?.agent_type || "Sub-agent";
  const activity = info?.description
    || (info?.last_tool_name
      ? toolMeta[info.last_tool_name]?.label ?? info.last_tool_name
      : null);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-space/60 backdrop-blur-sm p-4"
      onClick={close}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`${title} activity`}
        tabIndex={-1}
        className="bg-surface rounded-2xl shadow-xl w-full max-w-2xl max-h-[80vh]
                   flex flex-col overflow-hidden outline-none
                   animate-[scaleIn_120ms_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — who this is and what it's doing right now */}
        <div className="flex items-start justify-between gap-3 px-5 py-3 border-b border-space-30/60 bg-jove-10">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span aria-hidden>🤖</span>
              <h2 className="text-sm font-semibold text-jove truncate">{title}</h2>
              {isRunning ? (
                <span className="flex items-center gap-1 text-[10px] text-space-50 shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-jove animate-pulse" />
                  running
                </span>
              ) : null}
            </div>
            {activity ? (
              <p className="text-xs text-space-50 truncate mt-0.5">{activity}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="shrink-0 text-space-50 hover:text-space transition-colors text-lg leading-none px-1"
          >
            ×
          </button>
        </div>

        {/* Body — the sub-agent's own stream */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {messages === null && !error ? (
            <p className="text-xs text-space-50 text-center py-6">Loading activity…</p>
          ) : error ? (
            <p className="text-xs text-space-50 text-center py-6">
              Could not load this sub-agent's activity.
            </p>
          ) : rows.length === 0 && !report ? (
            <p className="text-xs text-space-50 text-center py-6">
              {isRunning
                ? "This sub-agent hasn't reported anything yet…"
                : "This sub-agent recorded no activity."}
            </p>
          ) : (
            rows.map((row) => {
              if (row.kind === "thinking") {
                return (
                  <MessageThinkingPanel
                    key={row.id}
                    text={row.text}
                    durationMs={row.durationMs}
                  />
                );
              }
              if (row.kind === "tool") {
                return <ToolCallBlock key={row.id} toolCall={row.call} />;
              }
              return (
                <MessageBubble
                  key={row.msg.id}
                  message={row.msg}
                  entranceAnimation={false}
                  hideFeedback
                />
              );
            })
          )}

          {/* The sub-agent's answer. Rendered through MessageBubble so the
              markdown its reports are full of (tables, headings) formats the
              same way it would in chat. */}
          {reportMessage ? (
            <div className="pt-2 border-t border-space-30/60">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-space-40 mb-1.5">
                {report?.is_error ? "Returned an error" : "Final report"}
              </p>
              <MessageBubble
                message={reportMessage}
                entranceAnimation={false}
                hideFeedback
              />
            </div>
          ) : null}
        </div>

        {/* Footer — token/tool totals while live */}
        {info ? (
          <div className="flex items-center gap-3 px-5 py-1.5 border-t border-space-30/60 bg-space-20/40 text-[10px] text-space-50">
            <span>{info.tool_uses} tool {info.tool_uses === 1 ? "call" : "calls"}</span>
            {info.total_tokens > 0 ? <span>· {info.total_tokens.toLocaleString()} tokens</span> : null}
            <span className="ml-auto">Esc to close</span>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
